import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { CheckpointActionConditionType } from '@fi-shared/checklist/checklist-enum';
import {
    FiTooltipButtonIconColors,
    FiTooltipIconClassType,
} from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { CheckpointActionRecipientHighlightedTypes } from '@fi-shared/checklist/checklist-consts';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiImageSize, ObjectTypes } from '@fi-shared/types';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiAutomatedActionBase } from './automated-action-base.directive';
import { FiOnboardingStepNotificationReminderTime } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { INameIdObj } from '../fi-shared-interfaces';
@Component({
    selector: 'fi-automated-action-list-item',
    templateUrl: './action-list-item.component.html',
    styleUrls: ['./action-list-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-automated-action-list-item' },
    standalone: false,
})
export class FiAutomatedActionListItemComponent extends FiAutomatedActionBase {
    @Input() actionCondition: CheckpointActionConditionType;
    @Input() reminderTime: FiOnboardingStepNotificationReminderTime;
    @Input() taskName: string;
    @Input() recipientLabels: INameIdObj[] = CheckpointActionRecipientHighlightedTypes;

    protected getActionTargetLabel(): string {
        const selectedReceiversLabel = this.recipientLabels.find((option) => {
            return option.Id === this.recipientType;
        });
        return (
            this.appTextService.get(selectedReceiversLabel.Name) +
            (this.isLocalRolesRecipientType() || this.isRoleRecipientType() ? '' : '.')
        );
    }

    protected getActionConditionLabel(): string {
        switch (this._objectType) {
            case ObjectTypes.Checklist2:
                return this.getChecklistConditionLabel();
            case ObjectTypes.Onboarding:
                return this.getOnboardingConditionLabel();
            case ObjectTypes.Quiz:
                return this.getQuizConditionLabel();
            default:
                return '';
        }
    }

    private getChecklistConditionLabel(): string {
        const startLabel: string = this.appTextService.get('checklist.checkpoint_action_condition');
        switch (this.actionCondition) {
            case CheckpointActionConditionType.Ok:
                return startLabel + ' ' + this.appTextService.get('ok') + '.';
            case CheckpointActionConditionType.NotOk:
                return startLabel + ' ' + this.appTextService.get('notOk') + '.';
            case CheckpointActionConditionType.Answered:
                return this.appTextService.get('checklist.checkpoint_action_condition_checkpoint_answered');
            case CheckpointActionConditionType.NotFilledOut:
                return this.appTextService.get('checklist.checkpoint_action_condition_checkpoint_skipped');
            default:
                return '';
        }
    }

    protected getOnboardingConditionLabel(): string {
        switch (this.reminderTime) {
            case FiOnboardingStepNotificationReminderTime.Completed:
                return this.appTextService.get('onboarding.notification_viewer_step_completed');
            case FiOnboardingStepNotificationReminderTime.OneDay:
                return this.appTextService.get('onboarding.notification_viewer_step_no_answer_one_day');
            case FiOnboardingStepNotificationReminderTime.ThreeDays:
                return this.appTextService.get('onboarding.notification_viewer_step_no_answer_three_days');
            case FiOnboardingStepNotificationReminderTime.SevenDays:
                return this.appTextService.get('onboarding.notification_viewer_step_no_answer_seven_days');
            default:
                return '';
        }
    }

    protected getQuizConditionLabel(): string {
        return this.appTextService.get('quiz.notification_case');
    }

    protected readonly FiTooltipButtonIconColors = FiTooltipButtonIconColors;
    protected readonly FiProfileSizes = FiProfileSizes;
    protected readonly FiImageSize = FiImageSize;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly FiTooltipIconClassType = FiTooltipIconClassType;
}
